import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Eye, ChevronLeft, ChevronRight, FileText, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

const STATUS_FILTERS = [
  { value: '', label: 'Tous' },
  { value: 'paid', label: 'Payée' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échouée' },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
  }, [user]);

  useEffect(() => {
    if (user) fetchInvoices();
  }, [user, page, statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_URL}/api/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setInvoices(data.invoices || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoiceId) => {
    setDownloadLoading(invoiceId);
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-${invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleViewDetail = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setDetailInvoice(data);
    } catch (err) {
      toast.error('Erreur lors du chargement du détail');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatPrice = (cents, currency) => {
    if (cents == null) return '—';
    const amount = (cents / 100).toFixed(2);
    const symbol = currency === 'eur' ? '€' : '$';
    return `${amount} ${symbol}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">Payée</span>;
      case 'pending': return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">En attente</span>;
      case 'failed': return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">Échouée</span>;
      default: return <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-muted-foreground">{status}</span>;
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/home')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">Factures</h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-safe">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/5 text-muted-foreground hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Invoice List */}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="spinner" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <FileText size={40} className="mb-2 opacity-30" />
            <p className="text-sm">Aucune facture trouvée</p>
          </div>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv, i) => (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="card p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {inv.number || `Facture #${inv.id}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(inv.created_at || inv.date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-primary">
                      {formatPrice(inv.total_cents || inv.amount_cents, inv.currency)}
                    </p>
                    {getStatusBadge(inv.status)}
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <button
                    onClick={() => handleViewDetail(inv.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-white/5 text-muted-foreground hover:text-white hover:bg-white/10 transition-all"
                  >
                    <Eye size={14} /> Voir
                  </button>
                  <button
                    onClick={() => handleDownload(inv.id)}
                    disabled={downloadLoading === inv.id}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all disabled:opacity-50"
                  >
                    {downloadLoading === inv.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    Télécharger
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 rounded-lg bg-white/5 disabled:opacity-30 hover:bg-white/10 transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 rounded-lg bg-white/5 disabled:opacity-30 hover:bg-white/10 transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* Counter */}
        {!loading && invoices.length > 0 && (
          <p className="text-center text-xs text-muted-foreground">
            {total} facture{total > 1 ? 's' : ''} au total
          </p>
        )}
      </div>

      {/* Detail Modal */}
      {detailInvoice && (
        <>
          <div className="modal-overlay" onClick={() => setDetailInvoice(null)} />
          <div className="sheet">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display font-bold text-lg text-white">
                {detailInvoice.number || `Facture #${detailInvoice.id}`}
              </h2>
              <button onClick={() => setDetailInvoice(null)} className="p-1 rounded-lg hover:bg-white/5">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="text-white font-medium">{formatDate(detailInvoice.created_at || detailInvoice.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Statut</span>
                <span>{getStatusBadge(detailInvoice.status)}</span>
              </div>
              {detailInvoice.description && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Description</span>
                  <span className="text-white text-right max-w-[60%]">{detailInvoice.description}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="font-semibold text-white">Total</span>
                <span className="font-bold text-primary">
                  {formatPrice(detailInvoice.total_cents || detailInvoice.amount_cents, detailInvoice.currency)}
                </span>
              </div>
            </div>
            <button
              onClick={() => { handleDownload(detailInvoice.id); setDetailInvoice(null); }}
              className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
            >
              <Download size={16} /> Télécharger le PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}
